import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LockSimple, LockSimpleOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, localToday } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const monthLabel = (m) => (m ? new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "");
const prevMonth = () => { const d = new Date(`${localToday().slice(0, 7)}-01T00:00:00`); d.setMonth(d.getMonth() - 1); return d.toLocaleDateString("en-CA").slice(0, 7); };

const History = ({ rows }) => (
  <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground" data-testid="period-history">
    {rows.map((h, i) => (
      <li key={i} className="flex gap-2">
        {h.action === "close" ? <LockSimple size={13} className="mt-0.5 text-foreground" /> : <LockSimpleOpen size={13} className="mt-0.5 text-amber-600" />}
        <span><b className="text-foreground">{h.action === "close" ? "Closed through" : "Reopened from"} {monthLabel(h.month)}</b> · {h.by} · {new Date(h.at).toLocaleString()}{h.reason ? ` — ${h.reason}` : ""}</span>
      </li>
    ))}
  </ul>
);

export const ClosedPeriods = () => {
  const qc = useQueryClient();
  const [month, setMonth] = useState(prevMonth());
  const [reopen, setReopen] = useState("");
  const [reason, setReason] = useState("");
  const { data } = useQuery({ queryKey: ["period-lock"], queryFn: async () => (await api.get("/periods/lock")).data });
  const locked = data?.locked_through || "";
  const done = (msg) => (r) => { qc.setQueryData(["period-lock"], r); toast.success(msg); setReason(""); };
  const onErr = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
  const close = useMutation({ mutationFn: async () => (await api.post("/periods/close", { month })).data, onSuccess: done(`Closed through ${monthLabel(month)}`), onError: onErr });
  const open = useMutation({ mutationFn: async () => (await api.post("/periods/reopen", { month: reopen || locked, reason })).data, onSuccess: done("Month reopened"), onError: onErr });
  return (
    <div className="lg:col-span-3 border border-border bg-card p-5" data-testid="closed-periods-card">
      <p className="font-bold text-sm mb-1 flex items-center gap-2"><LockSimple size={16} className="text-primary" /> Closed Periods</p>
      <p className="text-xs text-muted-foreground mb-4">Closing a month stops anything being posted with a date in that month or earlier — shipments, invoices, credit notes, receipts, purchase invoices, stock transfers and returns — for everyone, administrators included.</p>
      <p className="text-sm mb-4" data-testid="period-locked-through">{locked ? <>Closed through <b>{monthLabel(locked)}</b></> : "No months are closed."}</p>
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="overline-label">Close through</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 rounded-sm h-9 w-44" data-testid="period-close-month" /></div>
          <Button onClick={() => close.mutate()} disabled={!month || close.isPending} className="rounded-sm h-9" data-testid="period-close-button"><LockSimple size={15} className="mr-1.5" /> Close month</Button>
        </div>
        {locked && (
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label className="overline-label">Reopen from</Label><Input type="month" value={reopen || locked} max={locked} onChange={(e) => setReopen(e.target.value)} className="mt-1 rounded-sm h-9 w-44" data-testid="period-reopen-month" /></div>
            <div><Label className="overline-label">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it's being reopened" className="mt-1 rounded-sm h-9 w-64" data-testid="period-reopen-reason" /></div>
            <Button variant="outline" onClick={() => open.mutate()} disabled={!reason.trim() || open.isPending} className="rounded-sm h-9" data-testid="period-reopen-button"><LockSimpleOpen size={15} className="mr-1.5" /> Reopen</Button>
          </div>
        )}
      </div>
      {locked && <p className="text-[11px] text-muted-foreground mt-2">Reopening a month also reopens every month after it.</p>}
      {data?.history?.length > 0 && <History rows={data.history} />}
    </div>
  );
};
